export const railStations = [
  {
    id: "amtrak-nyp",
    name: "New York Penn Station",
    city: "New York",
    code: "NYP",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "amtrak-was",
    name: "Washington Union Station",
    city: "Washington DC",
    code: "WAS",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "amtrak-bos",
    name: "Boston South Station",
    city: "Boston",
    code: "BOS",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "amtrak-chi",
    name: "Chicago Union Station",
    city: "Chicago",
    code: "CHI",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "amtrak-phl",
    name: "Philadelphia 30th Street Station",
    city: "Philadelphia",
    code: "PHL",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "amtrak-lax",
    name: "Los Angeles Union Station",
    city: "Los Angeles",
    code: "LAX",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "amtrak-sea",
    name: "Seattle King Street Station",
    city: "Seattle",
    code: "SEA",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "amtrak-pdx",
    name: "Portland Union Station",
    city: "Portland",
    code: "PDX",
    country: "United States",
    brands: ["Amtrak"],
  },
  {
    id: "via-tor",
    name: "Toronto Union Station",
    city: "Toronto",
    code: "TWO",
    country: "Canada",
    brands: ["VIA Rail"],
  },
  {
    id: "via-mtl",
    name: "Montreal Central Station",
    city: "Montreal",
    code: "MTRL",
    country: "Canada",
    brands: ["VIA Rail"],
  },
  {
    id: "via-ott",
    name: "Ottawa Station",
    city: "Ottawa",
    code: "OTTW",
    country: "Canada",
    brands: ["VIA Rail"],
  },
  {
    id: "via-qbc",
    name: "Quebec City Gare du Palais",
    city: "Quebec City",
    code: "QBEC",
    country: "Canada",
    brands: ["VIA Rail"],
  },
  {
    id: "via-vnc",
    name: "Vancouver Pacific Central Station",
    city: "Vancouver",
    code: "VAC",
    country: "Canada",
    brands: ["VIA Rail"],
  },
  {
    id: "via-wpg",
    name: "Winnipeg Union Station",
    city: "Winnipeg",
    code: "WPEG",
    country: "Canada",
    brands: ["VIA Rail"],
  },
];

const normalize = (value) => value.trim().toLowerCase();

export const findRailStation = (value) => {
  const normalized = normalize(value || "");
  return railStations.find(
    (station) =>
      normalize(station.code) === normalized ||
      normalize(station.name) === normalized ||
      normalize(station.city) === normalized ||
      normalize(`${station.name} (${station.code})`) === normalized,
  );
};

export const searchRailStations = (query, brand = "any") => {
  const normalized = normalize(query || "");

  if (normalized.length < 2) {
    return [];
  }

  return railStations
    .filter((station) => brand === "any" || station.brands.includes(brand))
    .filter((station) =>
      [station.name, station.city, station.code, station.country]
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    )
    .slice(0, 8);
};

export const formatRailStation = (station) =>
  station ? `${station.name} (${station.code})` : "";

const routeProfiles = [
  {
    origin: "NYP",
    dest: "WAS",
    brand: "Amtrak",
    train: "Northeast Regional",
    duration: "3h 22m",
    prices: [39, 64, 108],
  },
  {
    origin: "NYP",
    dest: "BOS",
    brand: "Amtrak",
    train: "Acela / Northeast Regional",
    duration: "3h 49m",
    prices: [48, 79, 138],
  },
  {
    origin: "SEA",
    dest: "PDX",
    brand: "Amtrak",
    train: "Cascades",
    duration: "3h 30m",
    prices: [31, 49, 72],
  },
  {
    origin: "TWO",
    dest: "MTRL",
    brand: "VIA Rail",
    train: "Corridor",
    duration: "5h 11m",
    prices: [54, 78, 119],
  },
  {
    origin: "MTRL",
    dest: "OTTW",
    brand: "VIA Rail",
    train: "Corridor",
    duration: "2h 02m",
    prices: [37, 56, 84],
  },
  {
    origin: "VAC",
    dest: "WPEG",
    brand: "VIA Rail",
    train: "The Canadian",
    duration: "2d 5h",
    prices: [242, 389, 612],
  },
];

const fallbackProfiles = {
  Amtrak: {
    brand: "Amtrak",
    train: "Intercity Rail",
    duration: "3h 45m",
    prices: [42, 68, 96],
  },
  "VIA Rail": {
    brand: "VIA Rail",
    train: "Corridor / Long-distance Rail",
    duration: "4h 20m",
    prices: [49, 74, 112],
  },
};

const buildDepartures = (profile, passengers) =>
  ["07:10", "12:45", "17:30"].map((departTime, index) => ({
    id: `${profile.brand}-${profile.train}-${departTime}`.replace(/\W+/g, "-"),
    brand: profile.brand,
    train: profile.train,
    departTime,
    arriveTime: ["10:32", "16:07", "20:52"][index],
    duration: profile.duration,
    fare: profile.prices[index] * passengers,
    seatsLeft: [8, 5, 11][index],
  }));

export const getRailResults = ({
  origin,
  dest,
  brand = "any",
  passengers = "1",
}) => {
  const originStation = findRailStation(origin);
  const destStation = findRailStation(dest);
  const passengerCount = Number(passengers) || 1;

  if (!originStation || !destStation) {
    return [];
  }

  const originCode = originStation.code;
  const destCode = destStation.code;
  const matchingProfile = routeProfiles.find(
    (profile) =>
      ((profile.origin === originCode && profile.dest === destCode) ||
        (profile.origin === destCode && profile.dest === originCode)) &&
      (brand === "any" || profile.brand === brand),
  );

  if (matchingProfile) {
    return buildDepartures(matchingProfile, passengerCount);
  }

  const sharedBrand =
    brand !== "any"
      ? brand
      : originStation.brands.find((item) => destStation.brands.includes(item));

  if (!sharedBrand) {
    return [];
  }

  return buildDepartures(fallbackProfiles[sharedBrand], passengerCount);
};
